"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";

type Message = {
  id: number;
  sender: string | null;
  content: string | null;
  received_at: number; // unix seconds
};

type Page = {
  messages: Message[];
  hasMore: boolean;
  nextBefore: number | null;
};

const REFRESH_SECONDS = 60;

function relativeTime(ts: number): string {
  const diff = Math.floor(Date.now() / 1000 - ts);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} days ago`;
}

function fullDateTime(ts: number): string {
  return new Date(ts * 1000).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Numeric senders become international format; alphanumeric stay as-is. */
function formatSender(sender: string | null): string {
  if (!sender) return "Unknown";
  return /^\d{7,15}$/.test(sender) ? `+${sender}` : sender;
}

function copyText(value: string, label: string) {
  navigator.clipboard.writeText(value);
  toast.success(`${label} Copied`);
}

function CopyIcon({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
      />
    </svg>
  );
}

// Tokenizer for message bodies: emails and URLs become links, likely
// verification codes (4-8 digit standalone numbers) get a copy button.
const TOKEN_RE = new RegExp(
  [
    "([\\w.+-]+@[\\w-]+(?:\\.[\\w-]+)+)", // 1: email
    "((?:https?:\\/\\/|www\\.)[^\\s]+|\\b[a-z0-9][a-z0-9-]*(?:\\.[a-z0-9-]+)*\\.[a-z]{2,}(?:\\/[^\\s]*)?)", // 2: url / bare domain
    "(\\b\\d{4,8}\\b)", // 3: verification code
  ].join("|"),
  "gi"
);

function renderContent(content: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of content.matchAll(TOKEN_RE)) {
    const index = match.index ?? 0;
    if (index > last) nodes.push(content.slice(last, index));
    const [token, email, url, code] = match;
    if (email) {
      nodes.push(
        <a
          key={key++}
          href={`mailto:${email}`}
          className="text-blue-600 underline break-all"
        >
          {email}
        </a>
      );
    } else if (url) {
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      nodes.push(
        <a
          key={key++}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline break-all"
        >
          {url}
        </a>
      );
    } else if (code) {
      nodes.push(
        <span
          key={key++}
          className="inline-flex items-baseline gap-1 font-semibold text-gray-900"
        >
          {code}
          <button
            onClick={() => copyText(code, "Code")}
            title="Copy code"
            className="relative top-0.5 text-gray-400 hover:text-primary"
          >
            <CopyIcon />
          </button>
        </span>
      );
    } else {
      nodes.push(token);
    }
    last = index + token.length;
  }
  if (last < content.length) nodes.push(content.slice(last));
  return nodes;
}

export default function MessagesReceived({ endpoint }: { endpoint: string }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_SECONDS);
  const initialized = useRef(false);

  const joiner = endpoint.includes("?") ? "&" : "?";

  const fetchLatest = useCallback(async () => {
    try {
      const res = await fetch(endpoint);
      if (!res.ok) return; // keep existing rows; retry next tick
      const page: Page = await res.json();
      setMessages((prev) => {
        if (!initialized.current) return page.messages;
        const seen = new Set(prev.map((m) => m.id));
        const fresh = page.messages.filter((m) => !seen.has(m.id));
        return fresh.length ? [...fresh, ...prev] : prev;
      });
      if (!initialized.current) {
        setHasMore(page.hasMore);
        setNextBefore(page.nextBefore);
        initialized.current = true;
      }
    } catch {
      // network hiccup: keep existing rows, retry on the next countdown
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  const handleManualRefresh = useCallback(() => {
    setCountdown(REFRESH_SECONDS);
    fetchLatest();
  }, [fetchLatest]);

  const loadMore = useCallback(async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetch(`${endpoint}${joiner}before=${nextBefore}`);
      if (!res.ok) return;
      const page: Page = await res.json();
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...prev, ...page.messages.filter((m) => !seen.has(m.id))];
      });
      setHasMore(page.hasMore);
      setNextBefore(page.nextBefore);
    } catch {
      // leave state unchanged; user can press Load More again
    } finally {
      setLoadingMore(false);
    }
  }, [endpoint, joiner, nextBefore, loadingMore]);

  useEffect(() => {
    initialized.current = false;
    setLoading(true);
    fetchLatest();
    const timer = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) {
          fetchLatest();
          return REFRESH_SECONDS;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [fetchLatest]);

  return (
    <div className="my-14 flow-root space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-3xl font-bold text-gray-900">Messages Received</h2>
        <button
          onClick={handleManualRefresh}
          title="Refresh now"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-primary border rounded-lg px-3 py-1.5 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
            />
          </svg>
          Refresh now &middot; auto in {countdown}s
        </button>
      </div>

      <div>
        <div className="bg-white rounded-lg shadow-lg">
          <div className="inline-block min-w-full py-2 align-top">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="w-44 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-600 sm:pl-3"
                  >
                    RECEIVED
                  </th>
                  <th
                    scope="col"
                    className="w-36 px-3 py-3.5 text-left text-sm font-semibold text-gray-600"
                  >
                    SENDER
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-600"
                  >
                    MESSAGE
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-sm text-gray-400">
                      Loading messages…
                    </td>
                  </tr>
                ) : messages.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-sm text-gray-500">
                      No messages received yet.
                    </td>
                  </tr>
                ) : (
                  messages.map((message) => (
                    <tr key={message.id} className="even:bg-gray-50 align-top">
                      <td className="w-44 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                        {fullDateTime(message.received_at)}
                        <span className="block text-xs text-gray-400">
                          ({relativeTime(message.received_at)})
                        </span>
                      </td>
                      <td className="w-36 px-3 py-4 text-sm text-gray-500 break-words">
                        {formatSender(message.sender)}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500 whitespace-pre-wrap break-words">
                        {message.content ? renderContent(message.content) : ""}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {hasMore && (
          <div className="flex justify-center my-4">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="disabled:opacity-60 bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:from-[#090EDB]/80 hover:to-[#E73DC4]/80 px-8 py-2 text-white text-sm font-semibold rounded-lg transition-all duration-300"
            >
              {loadingMore ? "Loading…" : "Load More"}
            </button>
          </div>
        )}

        <p className="text-sm my-4 text-gray-600">
          * SMS messages received for this number, newest first
        </p>
      </div>
    </div>
  );
}
